import { useState } from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button, Grid, NotFound, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import {
  ProjectForSelectFragment,
  ProjectManagementProjectDetailsPageOverviewPageContextlessPoliciesFragment,
  ProjectManagementProjectDetailsPageOverviewPageProjectFragment,
  ProjectManagementProjectDetailsPageQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { PickerSelector } from 'modules/core/components/DateNavigator/_DateRangeSelector/_PickerSelector'
import { Popover } from 'modules/core/components/DateNavigator/_Popover'
import Filter from 'modules/core/components/Filter'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { SalesProductModal } from 'modules/finance/components/upsell/SalesProductModal'
import { InsightCard } from 'modules/project_management/components/InsightCard'
import { DistributionChart } from 'modules/project_management/components/charts/DistributionChart'
import { EvolutionChart } from 'modules/project_management/components/charts/EvolutionChart'
import { UpsellProjectsOverviewLanding } from 'modules/project_management/components/upsells/UpsellProjectsOverviewLanding'
import { useGetProjectTimeRanges } from 'modules/project_management/hooks/graphs/useGetProjectTimeRanges'
import { useGetSelectedGraphDates } from 'modules/project_management/hooks/graphs/useGetSelectedGraphDates'
import {
  overviewFilterOptionsEnum,
  overviewFrequencyOptionsEnum,
} from 'modules/project_management/lib/constants'
import {
  OverviewFilterOption,
  currentWeekFilterOption,
  overviewFilterOptions,
  overviewFilterWholeProject,
  overviewFrequencyOption,
  overviewFrequencyOptions,
} from 'modules/project_management/lib/overviewOptions'

import FinancialDetailsSection from './FinancialDetailsSection'
import ProjectStatusSection from './ProjectStatusSection'

const OverviewPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()
  const [crmUpsellBannerIsHidden, setCrmUpsellBannerIsHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideProjectsCrmUpsellBannerOnCreateProject
  )
  const [showCrmUpsellModal, setShowCrmUpsellModal] = useState(
    () => !!location.state?.showCrmUpsell && !crmUpsellBannerIsHidden
  )

  const { employee } = useCurrentSession()
  const {
    attendanceContextlessPolicies,
    completedTasksCount,
    contextlessPolicies,
    project,
    hasBudgetFeature,
    hasExpenses,
    hasFlexibleTracking,
    hasOverviewEnabled,
    hasSubprojectsEntityFeature,
    hasPendingSpendings,
    overdueTasksCount,
    ratesBreakdowns,
    spendingCostsApprovedAmountCents,
    taskTotalCount,
  } = useOutletContext<{
    attendanceContextlessPolicies: ProjectManagementProjectDetailsPageQuery['attendance']['_policies']
    completedTasksCount: number
    contextlessPolicies: ProjectManagementProjectDetailsPageOverviewPageContextlessPoliciesFragment['_policies']
    project: ProjectManagementProjectDetailsPageOverviewPageProjectFragment
    hasBudgetFeature: boolean
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    hasOverviewEnabled: boolean
    hasSubprojectsEntityFeature: boolean
    hasPendingSpendings: boolean
    overdueTasksCount: number
    ratesBreakdowns: ProjectManagementProjectDetailsPageQuery['projectManagement']['ratesBreakdownsConnection']['nodes']
    spendingCostsApprovedAmountCents: number
    taskTotalCount: number
  }>()
  const [selectedFilter, setSelectedFilter] = useState<OverviewFilterOption | undefined>(
    currentWeekFilterOption()
  )
  const [selectedFrequency, setSelectedFrequency] = useState<overviewFrequencyOption | undefined>(
    overviewFrequencyOptions()[0]
  )

  const canSeeCosts = contextlessPolicies.projectSeeCost.can
  const canSeeLaborCosts = contextlessPolicies.projectSeeLaborCost.can
  const canManageRates = contextlessPolicies.rateManage.can
  const hasTimeTrackingSection =
    attendanceContextlessPolicies.sidebarSeeAttendanceCompanyTopLevelItem.can

  const canExportData =
    contextlessPolicies.projectManage.can &&
    !!employee &&
    (hasTimeTrackingSection || hasExpenses || hasFlexibleTracking)

  const { projectTimeRanges } = useGetProjectTimeRanges({ projectId: Number(id) })

  const [customStartDate, setCustomStartDate] = useState<Date>(new Date())
  const [customEndDate, setCustomEndDate] = useState<Date>(new Date())

  const hasCustomRange =
    selectedFilter?.value === overviewFilterOptionsEnum.customRange &&
    customStartDate &&
    customEndDate
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [frequencyPopoverOpen, setFrequencyPopoverOpen] = useState(false)
  const filteredRanges = useGetSelectedGraphDates(selectedFilter?.value, projectTimeRanges)

  useEffect(() => {
    track(trackingEvents.projectManagement.overview.visitOverviewSection, {
      graphOrLanding: hasOverviewEnabled ? 'graph' : 'landing',
      can_see_costs: canSeeCosts,
      can_see_labor_costs: canSeeLaborCosts,
    })
  }, [hasOverviewEnabled, canSeeCosts, canSeeLaborCosts])

  useEffect(() => {
    track(trackingEvents.projectManagement.overview.projectsOverviewFilterSelected, {
      filterType: selectedFilter?.value,
    })
  }, [selectedFilter?.value])

  useEffect(() => {
    if (location.state?.showCrmUpsell && !crmUpsellBannerIsHidden) {
      setShowCrmUpsellModal(true)
      window.history.replaceState({}, '')
    }
  }, [crmUpsellBannerIsHidden])

  const findOption = (value: string | undefined) => {
    if (value === overviewFilterOptionsEnum.customRange) {
      handleOpenPopover()
    }
    return overviewFilterOptions().find((option) => option.value === value)
  }

  const findFrequency = (value: string | undefined) => {
    return overviewFrequencyOptions().find((option) => option.value === value)
  }

  // custom range logic here
  const customRange = {
    startDate: moment(customStartDate),
    endDate: moment(customEndDate),
    slotType: selectedFrequency?.value,
  }

  const selectedDates = hasCustomRange ? customRange : filteredRanges

  const handleOpenPopover = useUnsafeCallback(() => {
    setPopoverOpen(true)
  }, [])

  const handleClosePopover = useUnsafeCallback(() => {
    setPopoverOpen(false)
  }, [])

  const handleCloseFrequencyPopover = useUnsafeCallback(() => {
    setFrequencyPopoverOpen(false)
  }, [])

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setCustomStartDate(startDate)
    setCustomEndDate(endDate)
    handleClosePopover()
  }

  if (!project) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  const projectForSelect: ProjectForSelectFragment = {
    id: project.id,
    code: project.code,
    name: project.name,
    subprojectsConnection: {
      totalCount: null,
    },
  }

  const imputedMinutes = project.inputedMinutes || 0
  const currency = project.legalEntity.currency
  const isBillable = project.isBillable
  const laborCost = Number(project.laborCostCents) || 0
  const projectCost =
    (Number(project.fixedCostCents) || 0) + laborCost + (Number(project.spendingCostCents) || 0)

  const getTrackedHoursInfo = () => {
    if (project.fixedCostPlanning)
      return i18n.t(
        'project_management.overview.sections.financial_details.fixed_price_type.tooltip_message'
      )
    if (project.budgetStrategy && project.budgetStrategy.budgetType === 'total_budget')
      return i18n.t(
        'project_management.overview.sections.financial_details.time_and_materials_type.tooltip_message'
      )
    return i18n.t(
      'project_management.overview.sections.financial_details.no_budget_type.tooltip_message'
    )
  }

  return (
    <>
      <SalesProductModal
        isOpen={showCrmUpsellModal}
        onClose={() => {
          setShowCrmUpsellModal(false)
          setCrmUpsellBannerIsHidden(true)
        }}
        requestSource="sales_from_project_details_on_create_project"
        pageSource={location.pathname}
      />

      <Box flexDirection="column" gap="s24">
        {canManageRates && (
          <>
            <FinancialDetailsSection
              budgetType={project.budgetStrategy?.budgetType}
              costToDate={projectCost}
              currency={currency}
              fixedCosts={Number(project.fixedCostCents) || 0}
              fixedCostPlannedIncome={
                project.budgetStrategy?.budgetType === 'project_fixed_cost'
                  ? project.budgetStrategy?.feeAmountCents || 0
                  : undefined
              }
              fixedCostPlannedCents={
                project.budgetStrategy?.budgetType === 'project_fixed_cost'
                  ? project.budgetStrategy?.plannedCents || 0
                  : undefined
              }
              hasPendingSpendings={hasPendingSpendings}
              hoursPerBillRate={ratesBreakdowns[0]?.hoursPerBillRate || 0}
              hoursPerCostRate={laborCost}
              isBillable={isBillable}
              spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
              timeAndMaterialsTotalBudget={
                project.budgetStrategy?.budgetType === 'total_budget'
                  ? (hasSubprojectsEntityFeature
                      ? project.budgetStrategy?.feeAmountCents
                      : project.budgetStrategy?.plannedCents) || 0
                  : undefined
              }
            />
            <ProjectStatusSection
              completedTasks={completedTasksCount}
              imputedMinutes={imputedMinutes}
              plannedMinutes={project.budgetStrategy?.plannedMinutes || 0}
              navigateToTasks={() =>
                navigate(
                  navigation.projects.id.details.tasks.fullPath({
                    id: Number(id),
                  })
                )
              }
              overdueTasks={overdueTasksCount}
              totalTasks={taskTotalCount}
              trackedHoursInfo={getTrackedHoursInfo()}
            />
          </>
        )}
        {!canSeeCosts && (
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 12, s: 4 }}>
              <InsightCard.Hours imputedMinutes={imputedMinutes} />
            </Box>
          </Grid>
        )}
        <Text size="300" weight="semibold" color="grey1000">
          {i18n.t('project_management.overview.sections.graphs.title')}
        </Text>
        {hasOverviewEnabled ? (
          <Box paddingBottom="s12" flexDirection="column" gap="s12">
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Box flexDirection="row" justifyContent="flexStart" gap="s8">
                <Popover
                  open={popoverOpen}
                  onClose={handleClosePopover}
                  trigger={
                    <Filter.SingleChoice
                      label={i18n.t('common.date_date')}
                      options={overviewFilterOptions()}
                      defaultOpen={false}
                      selectedOption={selectedFilter}
                      onValueChange={(value) => setSelectedFilter(findOption(value))}
                    />
                  }
                >
                  <PickerSelector
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onDateRangeChange={handleDateRangeChange}
                  />
                </Popover>
                <Popover
                  open={frequencyPopoverOpen}
                  onClose={handleCloseFrequencyPopover}
                  trigger={
                    <Filter.SingleChoice
                      label={i18n.t('project_management.graphs.details.frequency')}
                      options={overviewFrequencyOptions()}
                      defaultOpen={false}
                      selectedOption={selectedFrequency}
                      onValueChange={(value) => setSelectedFrequency(findFrequency(value))}
                    />
                  }
                />
              </Box>
              {canExportData && (
                <Box flexDirection="row" gap="s8" alignItems="center">
                  <PopoverMenu type="list">
                    <PopoverMenu.Trigger>
                      <Button.Decorative type="secondary" iconName="ellipsis" />
                    </PopoverMenu.Trigger>
                    <PopoverMenu.Content side="bottom" align="end">
                      <PopoverMenu.TouchableOption
                        title={i18n.t(
                          'project_management.table.projects_list.table_options.export.title'
                        )}
                        description={i18n.t(
                          'project_management.table.projects_list.table_options.export.description'
                        )}
                        onClick={() => {
                          navigate(
                            navigation.projects.id.details.overview.export.fullPath({
                              id: project.id,
                            }),
                            { state: { projectForSelect } }
                          )
                        }}
                      />
                    </PopoverMenu.Content>
                  </PopoverMenu>
                </Box>
              )}
            </Box>
            <EvolutionChart
              projectId={Number(id)}
              startDate={selectedDates.startDate}
              endDate={selectedDates.endDate}
              selectedFrequency={selectedFrequency?.value || overviewFrequencyOptionsEnum.daily}
              selectedFilter={selectedFilter || overviewFilterWholeProject()}
              plannedMinutes={
                project.fixedTimeBudget?.budgetAllocation
                  ? project.fixedTimeBudget?.budgetAllocation
                  : project.budgetStrategy?.plannedMinutes ?? undefined
              }
              plannedCents={
                project.fixedCostPlanning?.budgetAllocationCents
                  ? project.fixedCostPlanning?.budgetAllocationCents
                  : project.budgetStrategy?.budgetType === 'project_fixed_cost'
                  ? project.budgetStrategy?.plannedCents ?? undefined
                  : undefined
              }
              plannedIncome={
                project.fixedCostPlanning?.feeAmountCents
                  ? project.fixedCostPlanning?.feeAmountCents
                  : project.budgetStrategy?.feeAmountCents ?? undefined
              }
              canSeeCosts={canSeeCosts}
              canSeeLaborCosts={canSeeLaborCosts}
              currency={currency}
              hasBudgetFeature={hasBudgetFeature}
            />
            <DistributionChart
              projectId={Number(id)}
              startDate={selectedDates.startDate}
              endDate={selectedDates.endDate}
              selectedFrequency={selectedFrequency?.value || overviewFrequencyOptionsEnum.daily}
              selectedFilter={selectedFilter || overviewFilterWholeProject()}
              canSeeLaborCosts={canSeeLaborCosts}
              currency={currency}
            />
          </Box>
        ) : (
          <UpsellProjectsOverviewLanding />
        )}
        <Outlet
          context={{ attendanceContextlessPolicies, project, hasExpenses, hasFlexibleTracking }}
        />
      </Box>
    </>
  )
}

export default OverviewPage
